import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Payment } from '@turizi/shared';
import {
    IconBuildingBank,
    IconCreditCard,
    IconCash,
    IconBrandPaypal,
    IconBrandStripe,
    IconWallet,
    IconArrowsExchange,
    IconArrowDownLeft,
    IconArrowUpRight
} from '@tabler/icons-react';
import { formatPrice } from '@/lib/format-utils';
import { localTenant } from '@/services/session-service';

interface PaymentsSummaryProps {
    payments: Payment[];
    direction?: 'IN' | 'OUT';
}

const getPaymentMethodConfig = (method: string, t: any, iconSize = 10) => {
    const config: Record<string, { label: string; icon: React.ReactNode; colorClass: string }> = {
        bank_transfer: {
            label: t('payment_methods.bank_transfer'),
            icon: <IconBuildingBank size={iconSize} />,
            colorClass: 'text-blue-700 bg-blue-50/50 border-blue-100/50'
        },
        card: {
            label: t('payment_methods.card'),
            icon: <IconCreditCard size={iconSize} />,
            colorClass: 'text-indigo-700 bg-indigo-50/50 border-indigo-100/50'
        },
        cash: {
            label: t('payment_methods.cash'),
            icon: <IconCash size={iconSize} />,
            colorClass: 'text-emerald-700 bg-emerald-50/50 border-emerald-100/50'
        },
        paypal: {
            label: t('payment_methods.paypal'),
            icon: <IconBrandPaypal size={iconSize} />,
            colorClass: 'text-sky-700 bg-sky-50/50 border-sky-100/50'
        },
        stripe: {
            label: t('payment_methods.stripe'),
            icon: <IconBrandStripe size={iconSize} />,
            colorClass: 'text-purple-700 bg-purple-50/50 border-purple-100/50'
        },
        wallet_transfer: {
            label: t('payment_methods.wallet_transfer'),
            icon: <IconWallet size={iconSize} />,
            colorClass: 'text-gray-700 bg-gray-50/50 border-gray-100/50'
        },
        service_reciprocity: {
            label: t('payment_methods.service_reciprocity'),
            icon: <IconArrowsExchange size={iconSize} />,
            colorClass: 'text-amber-700 bg-amber-50/50 border-amber-100/50'
        },
        reconciling: {
            label: t('payment_methods.reconciling'),
            icon: <IconArrowsExchange size={iconSize} />,
            colorClass: 'text-slate-700 bg-slate-50/50 border-slate-100/50'
        }
    };

    return config[method] || {
        label: t(`payment_methods.${method}`),
        icon: <IconCreditCard size={iconSize} />,
        colorClass: 'text-gray-700 bg-gray-50/50 border-gray-100/50'
    };
};

export default function PaymentsSummary({ payments, direction }: PaymentsSummaryProps) {
    const { t } = useTranslation();
    const ownedBusinessIds = localTenant()?.businesses?.map(b => b.id) || [];

    const summary = useMemo(() => {
        if (!payments || payments.length === 0) return null;

        const totals = payments.reduce((acc, p) => {
            const currency = p.currency || '-';
            const method = p.payment_method || 'other';

            if (!acc[currency]) {
                acc[currency] = {
                    in: 0,
                    out: 0,
                    inByMethod: {} as Record<string, number>,
                    outByMethod: {} as Record<string, number>,
                };
            }

            const amount = Number(p.amount || 0);

            // Money In
            if (ownedBusinessIds.includes(p.to_business_id || -1)) {
                acc[currency].in += amount;
                acc[currency].inByMethod[method] = (acc[currency].inByMethod[method] || 0) + amount;
            }

            // Money Out
            if (ownedBusinessIds.includes(p.from_business_id || -1)) {
                acc[currency].out += amount;
                acc[currency].outByMethod[method] = (acc[currency].outByMethod[method] || 0) + amount;
            }

            return acc;
        }, {} as Record<string, {
            in: number;
            out: number;
            inByMethod: Record<string, number>;
            outByMethod: Record<string, number>;
        }>);

        const currencies = Object.keys(totals);

        return {
            totals,
            currencies
        };
    }, [payments, ownedBusinessIds]);

    const inCurrencies = useMemo(() => {
        if (!summary) return [];
        const withData = summary.currencies.filter(
            curr => (summary.totals[curr]?.in || 0) > 0 || Object.keys(summary.totals[curr]?.inByMethod || {}).length > 0
        );
        return withData.length > 0 ? withData : summary.currencies.slice(0, 1);
    }, [summary]);

    const outCurrencies = useMemo(() => {
        if (!summary) return [];
        const withData = summary.currencies.filter(
            curr => (summary.totals[curr]?.out || 0) > 0 || Object.keys(summary.totals[curr]?.outByMethod || {}).length > 0
        );
        return withData.length > 0 ? withData : summary.currencies.slice(0, 1);
    }, [summary]);

    if (!summary || summary.currencies.length === 0) return null;

    const { totals } = summary;
    const showIn = !direction || direction === 'IN';
    const showOut = !direction || direction === 'OUT';

    const renderCurrencyCard = (curr: string, type: 'IN' | 'OUT') => {
        const isIncome = type === 'IN';
        const total = isIncome ? totals[curr]?.in || 0 : totals[curr]?.out || 0;
        const methodsMap = isIncome ? totals[curr]?.inByMethod || {} : totals[curr]?.outByMethod || {};
        const rawMethods = Object.entries(methodsMap).filter(([_, val]) => val > 0);
        const sortedMethods = [...rawMethods].sort((a, b) => b[1] - a[1]);
        const totalDivisor = total || 1;

        return (
            <div
                key={`${type}-${curr}`}
                className="border border-slate-200/60 bg-white rounded-lg p-2 space-y-2"
            >
                {/* Method Breakdown */}
                <div className="flex-1 pt-1 sm:pt-0">
                    {rawMethods.length === 0 ? (
                        <div className="text-[10px] text-slate-400 font-normal italic py-1">
                            {t('no_transactions')}
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {sortedMethods.map(([method, val]) => {
                                const cfg = getPaymentMethodConfig(method, t);
                                const percentage = (val / totalDivisor) * 100;
                                return (
                                    <div
                                        key={method}
                                        className="flex justify-between items-center text-slate-600 text-xxs py-0.5 gap-2"
                                    >
                                        <div className="flex items-center gap-1.5">
                                            <span
                                                className={`inline-flex items-center justify-center p-0.5 rounded border ${cfg.colorClass}`}
                                            >
                                                {cfg.icon}
                                            </span>
                                            <span className="font-medium text-slate-600">{cfg.label}</span>
                                            <span className="text-[9px] text-slate-400 font-normal">
                                                ({percentage.toFixed(1)}%)
                                            </span>
                                        </div>
                                        <div className="flex-1 border-b border-dotted border-slate-200 mx-2 min-w-[12px]"></div>
                                        <span className="font-semibold text-slate-800 whitespace-nowrap">
                                            {formatPrice(val)}{' '}
                                            <span className="text-[9px] text-slate-400 font-normal uppercase">
                                                {curr}
                                            </span>
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Total Info */}
                <div className="flex flex-col justify-between">
                    <div className='flex gap-2 justify-between items-center p-1 bg-gray-100 rounded-md'>
                        <div className='text-slate-400 font-extrabold text-xs'>
                            {t('total')} {isIncome ? t('income') : t('outcome')} {curr}
                        </div>
                        <div className="text-sm font-bold flex items-baseline gap-0.5 leading-none">
                            <span className="font-light text-slate-400 text-xs">$</span>
                            <span>{formatPrice(total)}</span>
                            <span className="text-[10px] text-slate-400 uppercase font-normal ml-0.5">{curr}</span>
                        </div>
                    </div>
                </div>

            </div>
        );
    };

    return (
        <div className="flex flex-wrap gap-4 mb-6 items-start">
            {showIn && inCurrencies.map(curr => renderCurrencyCard(curr, 'IN'))}
            {showOut && outCurrencies.map(curr => renderCurrencyCard(curr, 'OUT'))}
        </div>
    );
}
